import clsx from "clsx"
import { ReactElement, ReactNode, useEffect, useRef, useState } from "react"
import { useNavigate } from "react-router-dom"
import { keycloak } from "../../lib/keycloak"
import { useAppDispatch, useAppSelector } from "../../lib/redux/hooks"
import { FolderIcon } from "../icons/FolderIcon"
import { GestPersonaleIcon } from "../icons/gestPersonaleIcon"
import { MenuIcon } from "../icons/menu"
import Avatar from "../molecules/avatar"
import Button from "../molecules/button"
import Breadcrumb from "../organisms/breadcrumb"
import { Header } from "../organisms/header"
import SideNav from "../organisms/navbar"
import { MenuItems } from "../organisms/navbar/dto"
import { MenuStatus } from "./dto"
import { setMenuStatus } from "./slice"

interface Props {
    children: ReactNode
    headerChildren?: ReactElement
    headerLabel?: string
    breadcrumbItems: string[]
    menuItem: MenuItems
    rightBar?: boolean
    overflow?: string
    noPaddingLat?: boolean
    headerIcon?: ReactElement
    customerName?: string | undefined
    customerCode?: string | undefined
    onScrollTop?: boolean
}

export function Layout(props: Props) {
    const { children, noPaddingLat } = props

    const layoutState = useAppSelector(state => state.layout)
    const userState = useAppSelector(state => state.users)
    const sessionState = useAppSelector(state => state.session)

    const navigate = useNavigate()
    const dispatch = useAppDispatch()

    const [isDropdownOpen, setIsDropdownOpen] = useState(false)
    const [showScrollButton, setShowScrollButton] = useState(false)

    const containerRef = useRef<HTMLDivElement | null>(null)
    const avatarRef = useRef<HTMLDivElement | null>(null)

    const scrollToTopContainer = (): void => {
        if (containerRef.current) {
            containerRef.current.scrollTo({ top: 0, behavior: 'smooth' })
        }
    }

    const handleAvatarClick = () => {
        setIsDropdownOpen(!isDropdownOpen)
    }

    useEffect(() => {
        if (props.onScrollTop) scrollToTopContainer()
    }, [props.onScrollTop])

    useEffect(() => {
        const container = containerRef.current
        if (!container) return
        const handleScroll = () => {
            if (container.scrollTop > (container.clientHeight / 2) - 150) {
                setShowScrollButton(true)
            } else {
                setShowScrollButton(false)
            }
        }
        container.addEventListener("scroll", handleScroll)
        return () => container.removeEventListener("scroll", handleScroll)
    }, [])

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (avatarRef.current && !avatarRef.current.contains(event.target as Node)) {
                setIsDropdownOpen(false)
            }
        }
        document.addEventListener("click", handleClickOutside)
        return () => document.removeEventListener("click", handleClickOutside)
    }, [])

    return (
        <div
            className="antialiased min-h-screen max-h-screen bg-bgGreen"
            style={{ display: 'flex', flexDirection: 'row', overflow: 'hidden', height: '100vh' }}
        >
            <div
                style={{ zIndex: 99 }}
                onMouseEnter={() => {
                    if (layoutState.menuStatus === MenuStatus.CLOSED)
                        dispatch(setMenuStatus(MenuStatus.HOVER))
                }}
                onMouseLeave={() => {
                    if (layoutState.menuStatus === MenuStatus.HOVER)
                        dispatch(setMenuStatus(MenuStatus.CLOSED))
                }}
                className={clsx(
                    "border-neutral-200 transition-all hidden lg:block shrink-0",
                    {
                        "w-[260px] min-w-[260px] transition-all":
                            layoutState.menuStatus === MenuStatus.OPEN || layoutState.menuStatus === MenuStatus.HOVER,
                        "w-[72px] min-w-[72px] transition-all":
                            layoutState.menuStatus === MenuStatus.CLOSED,
                        "absolute h-[100%] bg-white":
                            layoutState.menuStatus === MenuStatus.HOVER || layoutState.menuStatus === MenuStatus.CLOSED,
                    }
                )}
            >
                <SideNav />
            </div>
            <div
                className={clsx(
                    "border-neutral-200 transition-all lg:hidden",
                    {
                        "absolute h-[100%] w-[100%]": layoutState.menuStatus === MenuStatus.HOVER,
                        "hidden": layoutState.menuStatus !== MenuStatus.HOVER
                    }
                )}
            >
                <div
                    className="w-[100%] h-[100%] backdrop-blur-sm transition-all"
                    onClick={() => dispatch(setMenuStatus(MenuStatus.CLOSED))}
                >
                    <div
                        className="min-w-[260px] h-[100%] bg-white opacity-100 transition-all"
                        onClick={(e) => { e.preventDefault(); e.stopPropagation() }}
                    >
                        <SideNav />
                    </div>
                </div>
            </div>
            <div
                style={{ flexGrow: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}
                className={clsx(
                    "bg-brandPrimary-850",
                    {
                        'ml-[0px] lg:ml-[72px]': layoutState.menuStatus === MenuStatus.HOVER || layoutState.menuStatus === MenuStatus.CLOSED,
                        'ml-[0px]': layoutState.menuStatus === MenuStatus.OPEN,
                    }
                )}
            >
                <div
                    className="flex flex-row items-center justify-between bg-bgGreen"
                    style={{ padding: '0px 24px' }}
                >
                    <div className="block lg:hidden py-[20px]">
                        <div
                            className="cursor-pointer p-1.5 rounded-lg hover:bg-white/10 transition-colors"
                            onClick={() => dispatch(setMenuStatus(MenuStatus.HOVER))}
                        >
                            <MenuIcon color={""} size={22} />
                        </div>
                    </div>
                    <div className="hidden lg:flex h-[72px] items-center gap-6">
                        <Breadcrumb path={props.breadcrumbItems} menuItem={props.menuItem} />
                        {(sessionState.selected.customerName || sessionState.selected.customerCode) && (
                            <>
                                <div className="w-px h-5 bg-white/15" />
                                <span
                                    className="text-sm font-semibold tracking-wide"
                                    style={{ color: '#ff941a', fontFamily: 'Manrope' }}
                                >
                                    {sessionState.selected.customerName} &mdash; Cod. Cliente&nbsp;{sessionState.selected.customerCode}
                                </span>
                            </>
                        )}
                    </div>
                    <div className="flex flex-row gap-3 items-center">
                        <div className="flex flex-row gap-1">
                            <Button
                                size={"md"}
                                iconPosition={"only"}
                                variant={"link"}
                                color={"white"}
                                icon={<FolderIcon size={18} color={""} />}
                                onClick={() => navigate('/cartellaAziendale')}
                            />
                            <Button
                                size={"md"}
                                iconPosition={"only"}
                                variant={"link"}
                                color={"white"}
                                icon={<GestPersonaleIcon color={""} size={0} />}
                                onClick={() => navigate('/gestionePersonale')}
                            />
                        </div>
                        <div className="relative" ref={avatarRef}>
                            <div onClick={handleAvatarClick} className="cursor-pointer">
                                <Avatar
                                    type="single"
                                    size="sm"
                                    shape="circle"
                                    imageUrl=""
                                    altTextInitials={
                                        (userState.findMeResponse.name && userState.findMeResponse.surname)
                                            ? userState.findMeResponse.name[0] + userState.findMeResponse.surname[0]
                                            : 'A'
                                    }
                                />
                            </div>
                            {isDropdownOpen && (
                                <div
                                    onClick={(e) => {
                                        e.preventDefault()
                                        e.stopPropagation()
                                        keycloak.logout({ redirectUri: window.location.origin })
                                        localStorage.clear()
                                    }}
                                    className="absolute right-0 mt-2 z-10 bg-white rounded-xl shadow-lg border border-neutral-100 overflow-hidden w-[180px] cursor-pointer"
                                >
                                    <div className="flex items-center gap-3 px-4 py-3 hover:bg-neutral-50 transition-colors">
                                        <div className="w-2 h-2 rounded-full bg-red-400" />
                                        <span className="text-sm text-neutral-700 font-medium">
                                            Logout
                                        </span>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
                {props.headerLabel !== undefined && (
                    <Header
                        icon={props.headerIcon}
                        label={props.headerLabel}
                        customerCode={props.customerCode}
                        customerName={props.customerName}
                    >
                        {props.headerChildren}
                    </Header>
                )}
                {props.rightBar ? (
                    <div
                        ref={containerRef}
                        style={{
                            height: '100%',
                            flexGrow: 1,
                            padding: !children ? '24px' : '0px',
                            overflow: props.overflow,
                            overflowY: 'scroll'
                        }}
                        className="bg-brandPrimary-200"
                    >
                        {!children
                            ? <div className="w-full rounded-xl shadow-sm border border-neutral-200 h-full" />
                            : children
                        }
                    </div>
                ) : (
                    <div
                        ref={containerRef}
                        style={{
                            flexGrow: 1,
                            padding: !children ? '24px' : noPaddingLat === undefined ? '24px' : '0px',
                            overflowY: 'auto',
                            overflowX: 'hidden',
                        }}
                        className="bg-brandPrimary-200 border-t-[1px] border-t-neutral-200"
                    >
                        {!children
                            ? <div className="w-full rounded-xl shadow-sm border border-neutral-200 h-full" />
                            : children
                        }
                    </div>
                )}
                {showScrollButton && (
                    <button
                        onClick={scrollToTopContainer}
                        className="fixed bottom-6 right-6 bg-brandPrimary-500 text-white rounded-full w-10 h-10 flex items-center justify-center shadow-lg hover:bg-brandPrimary-700 transition-all hover:scale-105"
                        aria-label="Scroll to top"
                    >
                        ↑
                    </button>
                )}
            </div>
        </div>
    )
}